/**
 * Export Job Tracker
 * Monitor export job status and download completed exports
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExportJobTracker = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobs();
    // Poll for updates every 5 seconds
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/exports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'queued': 'bg-yellow-100 text-yellow-800',
      'processing': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'failed': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'queued': '🕒',
      'processing': '🔄',
      'completed': '✅',
      'failed': '❌'
    };
    return icons[status] || '❔';
  };

  return (
    <div className="p-8">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Export Job Tracker</h1>
          <p className="text-gray-600 mt-1">Monitor export generation progress</p>
        </div>
        <button
          onClick={fetchJobs}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Jobs List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {jobs.map(job => (
              <tr key={job.id}>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium capitalize">
                    {job.export_type.replace('_', ' ')}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}>
                    {getStatusIcon(job.status)} {job.status}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{width: `${job.progress_percentage || 0}%`}}
                    />
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {job.progress_percentage || 0}%
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {new Date(job.requested_at).toLocaleString()}
                </td>
                <td className="px-6 py-4">
                  {job.status === 'completed' && (
                    <button className="text-blue-600 hover:text-blue-800 font-medium">
                      📥 Download
                    </button>
                  )}
                  {job.status === 'failed' && (
                    <button className="text-red-600 hover:text-red-800 font-medium">
                      🔁 Retry
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {jobs.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
          No export jobs found. Generate credit packs or evidence packs to see them here.
        </div>
      )}

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          ℹ️ Jobs are processed in the background. 
          Completed exports are available for 30 days. 
          Page auto-refreshes every 5 seconds.
        </p>
      </div>
    </div>
  );
};

export default ExportJobTracker;